import React, { createContext, useContext, useState, useEffect } from 'react';
import apiService, { setAuthToken, getAuthToken } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Verificar se há usuário logado ao carregar a aplicação
  useEffect(() => {
    let isMounted = true; // Flag para evitar atualização em componente desmontado

    const checkUser = async () => {
      const token = getAuthToken();
      const savedUser = localStorage.getItem('user');

      // Se não há token OU não há usuário salvo, não faz requisição ao backend
      if (!token || !savedUser) {
        if (isMounted) {
          setUser(null);
          setLoading(false);
        }
        return;
      }

      // Validar se o token parece válido (formato JWT básico)
      const isValidTokenFormat = token && token.split('.').length === 3;
      if (!isValidTokenFormat) {
        // Token inválido, limpa dados e não faz requisição
        if (isMounted) {
          setUser(null);
          setAuthToken(null);
          localStorage.removeItem('user');
          setLoading(false);
        }
        return;
      }

      try {
        // Carrega usuário do cache para renderização rápida
        if (isMounted) {
          setUser(JSON.parse(savedUser));
        }

        // Valida o token com o backend (silenciosamente)
        const userData = await apiService.getCurrentUser();
        if (isMounted) {
          // Atualiza com os dados mais recentes do servidor
          setUser(userData.user);
          localStorage.setItem('user', JSON.stringify(userData.user));
        }
      } catch (error) {
        // Se o erro for de autenticação, limpa os dados silenciosamente
        if (error.message?.includes('401') || error.message?.includes('403')) {
          if (isMounted) {
            setUser(null);
            setAuthToken(null);
            localStorage.removeItem('user');
          }
        }
        // Se for outro erro (ex: rede), mantém o usuário do cache
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    checkUser();

    return () => {
      isMounted = false;
    };
  }, []);

  const login = async (credentials) => {
    try {
      // Validação básica
      if (!credentials.username || !credentials.password) {
        throw new Error('Usuário e senha são obrigatórios');
      }

      // Limpar caches antes de fazer login (evita dados de outro usuário)
      try {
        const { clearAllCache } = require('./QueryProvider');
        clearAllCache();
      } catch (e) {
        // Se falhar, ignora
      }
      sessionStorage.clear();

      // Fazer login usando o backend real
      const response = await apiService.login(credentials);

      // Salvar token no localStorage (usar accessToken retornado pelo backend)
      const token = response.accessToken || response.token;
      setAuthToken(token);

      // Salvar dados completos do usuário no localStorage
      const userData = response.user;
      localStorage.setItem('user', JSON.stringify(userData));

      // Definir usuário no estado
      setUser(userData);

      return userData;
    } catch (error) {
      // Limpar dados em caso de erro
      setAuthToken(null);
      localStorage.removeItem('user');
      setUser(null);
      throw error;
    }
  };

  const logout = async () => {
    try {
      // Invalidar token no backend
      await apiService.logout();
    } catch (error) {
      // Erro ao fazer logout no backend - continua limpando dados locais
    } finally {
      // Limpar estado do React
      setUser(null);

      // Limpar token de autenticação
      setAuthToken(null);

      // Limpar TODO o localStorage para evitar dados de outros usuários
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        // Manter apenas configurações de tema/preferências visuais
        if (key && !key.startsWith('theme') && !key.startsWith('darkMode')) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));

      // Limpar sessionStorage
      sessionStorage.clear();

      // Limpar cache do React Query (importar função do QueryProvider)
      try {
        const { clearAllCache } = require('./QueryProvider');
        clearAllCache();
      } catch (e) {
        // Falha silenciosa — cache será invalidado no próximo carregamento
      }
    }
  };

  const isAdmin = () => {
    return user?.role === 'admin';
  };

  const refreshUser = async () => {
    try {
      const userData = await apiService.getCurrentUser();
      setUser(userData.user);
      localStorage.setItem('user', JSON.stringify(userData.user));
      return userData.user;
    } catch (error) {
      throw error;
    }
  };

  const value = {
    user,
    loading,
    login,
    logout,
    isAdmin,
    refreshUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;